import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import moment from "moment";
import { Outlet, NavLink, useNavigate } from "react-router-dom";

// components
import { SectionHeader } from "../../components/layouts/section.header";
import { UserAvatar } from "../../components/commons/user.avatar";
import { StarRating } from "../../components/commons/star.rating";
import { BadgeStatus } from "../../components/commons/badge.status";
import { SpinnerTitle } from "../../components/commons/spinner.title";

// drawer
import { DrawerPartnerUploadedDocument } from "../../components/drawers/partner.uploaded.document";

// reducers
import { fetchPartnerAccount } from '../../stores/partner';
import { approveNewPartner } from "../../stores/applicant.partners";

// utilities
import { classNames, openAssetKeyNewTab } from "../../utilities";

export const ScreenPartnerDetails = () => {

  // hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { account_id = null } = useParams();
  const { 
    created, 
    status, 
    individual,
    application_location,
    date_of_birth,
    electrical_competency,
    registration_toolkit
  } = useSelector(context => context.partner);
  const { chargeman, electrical_wiring} = electrical_competency;

  // states
  const [loading, setLoading] = useState(true);
  const [drawerUploadedDoc, setDrawerUploadedDoc] = useState({
    open: false,
    url: null,
  });

  const onApprove = async (userId) => {
    try {
      setLoading(true);
      const res = await dispatch(approveNewPartner({
        user_id: userId,
      })).unwrap();
      console.debug('screens.partners.details@onApprove', 'res', res);

      // go back
      setTimeout(() => {
        navigate('/partners/registration')
      }, 2000)
    } catch (err) {
      console.error('screens.partners.details@onApprove', err);
    } finally {
      setLoading(false);
    }
  }

  const loaddata = async (userId) => {
    try {
      setLoading(true);
      const res = await dispatch(fetchPartnerAccount({
        user_id: userId,
      })).unwrap();
      console.debug('screens.partners.details@loaddata', 'res', res);
    } catch (err) {
      console.error('screens.partners.details@loaddata', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // sanity check
    if (!account_id) return;

    loaddata(account_id);
  }, [account_id])


  return (
    <>
      <Helmet>
        <title>Strategic Partner</title>
      </Helmet>
      <div className="py-8 h-screen w-full">

        {/* Header */}
        <div className="pb-8 px-8 grid grid-cols-10 gap-4 border-b border-slate-200">

          <div className="col-span-1 flex items-center justify-center">
            <UserAvatar url={individual.picture.key} />
          </div>

          <div className="col-span-6 flex flex-col justify-between items-start">
            <div className="flex flex-row justify-start items-center">
              <SectionHeader title={individual.name} />
              <div className="px-1 pl-4">
                <StarRating />
              </div>
              <div className="px-1">
                <BadgeStatus status={status}/>
              </div>
            </div>
            
            <div className="flex flex-row justify-end items-end">
              <HeaderKeyValue label={'Applied on'} value={new Date(created).toDateString()} />
              <HeaderKeyValue label={'Region'} value={`${application_location?.municipality}, ${application_location?.state}`} />
            </div>
          </div>

          <div className="col-span-3 flex items-start justify-end">

            {status === 'REGISTRATION:PENDING_APPROVAL' && 
              <div className="flex flex-row">
                {/* <button 
                  className="px-8 py-2 bg-red-500 rounded-xl shadow-md hover:cursor-pointer"
                  disabled={loading}
                >
                  {!loading && <p className="text-sm text-white font-bold">Reject Applicant</p>}
                  {loading && <SpinnerTitle spin={loading} />}
                </button> */}
                <button 
                  className="ml-4 px-8 py-2 bg-green-600 rounded-xl shadow-md hover:cursor-pointer"
                  disabled={loading}
                  onClick={() => onApprove(account_id)}
                >
                  {!loading && <p className="text-sm text-white font-bold">Approve Applicant</p>}
                  {loading && <SpinnerTitle spin={loading} />}
                </button>
              </div>
            }

          </div>
        </div>

        {/* BODY */}
        <div className="h1-full grid grid-cols-12 gap-4 bg-slate-50">

          {/* WORKING */}
          <div className="col-span-8 px-4 py-4">

            {/* FILTER */}
            <div className="flex flex-row justify-start items-center">
              <NavLink to='registration'>
                {(isActive) => (
                  <FilterSection title={'Application Form'} selected={isActive} />
                )}
              </NavLink>
              
              <FilterSection title={'Work Order'} />
              <FilterSection title={'Reviews'} />
              <FilterSection title={'Payment'} />
            </div>

            {/* SECTION OUTLET */}
            <div className="py-8">
              <Outlet />
            </div>
            
          </div>

          {/* INFORMATION */}
          <div className="col-span-4 overflow-y-auto bg-white border-l border-slate-200 shadow-md">

            {/* PERSONAL */}
            <div className="p-8 flex flex-col border-b border-slate-200">
              <h2 className="pb-4 text-base text-slate-700 font-semibold">Personal Information</h2>
              <SideKeyValue label={'Email'} value={individual.email} />
              <SideKeyValue label={'Phone Number'} value={individual.phone_number} />
              <SideKeyValue label={'Age'} value={`${moment().diff(moment(date_of_birth), 'years')} years old`} />
              <SideKeyValue label={'Birthdate'} value={new Date(date_of_birth).toLocaleDateString()} />
              <SideKeyValue 
                label={'Identification Number'} 
                value={
                  <div
                    onClick={() => openAssetKeyNewTab(individual.identification_number_image.key)}
                  >
                    <p className="underline text-blue-400 hover:cursor-pointer">{individual.identification_number}</p>
                  </div>
                } 
              />
              <SideKeyValue label={'Gender'} value={individual.gender} />
              <SideKeyValue label={'Race'} value={individual.race} />
              <SideKeyValue label={'Shirt Size'} value={registration_toolkit.shirt_size} />
              <SideKeyValue 
                label={'Living Address'} 
                value={
                  <div className="flex flex-col">
                    <p>{individual.address.poscode}, {individual.address.city}</p>
                    <p>{individual.address.state}</p>
                  </div>
                } 
              />
            </div>

            {/* WIRING COMPETENCY */}
            <div className="p-8 flex flex-col border-b border-slate-200">
              <h2 className="pb-4 text-base text-slate-700 font-semibold">Electrical Wiring Competency</h2>
              <SideKeyValue 
                label={'Highest Level Certification'} 
                value={electrical_wiring.not_applicable ? 'Not certified' : electrical_wiring.highest_level} 
              />
              {electrical_wiring.years_of_experience && 
                <SideKeyValue 
                  label={'Years of Experience'} 
                  value={electrical_wiring.years_of_experience.replace(/_/gi, ' ')} 
                />
              }
            </div>

            {/* CHARGEMAN COMPETENCY */}
            <div className="p-8 flex flex-col border-b border-slate-200">
              <h2 className="pb-4 text-base text-slate-700 font-semibold">Electrical Chargeman Competency</h2>
              <SideKeyValue 
                label={'Highest Level Certification'} 
                value={chargeman.not_applicable ? 'Not certified' : chargeman.highest_level} 
              />
              {chargeman.years_of_experience && 
                <SideKeyValue 
                  label={'Years of Experience'} 
                  value={chargeman.years_of_experience.replace(/_/gi, ' ')} 
                />
              }
            </div>

          </div>
        </div>
        
      </div>

      {/* DRAWERS */}
      <DrawerPartnerUploadedDocument 
        open={drawerUploadedDoc.open}
        setOpen={() => setDrawerUploadedDoc({open: false, url: null})}
      />
    </>
  );
};

const HeaderKeyValue = ({label, value}) => {
  return (
    <div className="pr-8">
      <h4 className="text-sm text-slate-500 font-normal">{label}</h4>
      <p className="text-base text-slate-800 font-semibold">{value}</p>
    </div>
  )
}


const SideKeyValue = ({label, value}) => {
  return (
    <div className="pt-4 grid grid-cols-8">
      <h4 className="col-span-4 text-sm text-slate-500 font-normal">{label}</h4>
      <span className="col-span-4 text-sm text-slate-900 font-medium capitalize text-left">{value}</span>
    </div>
  )
}

const FilterSection = ({title, selected, onSelect = () => {}}) => {
  return (
    <div className={classNames(
      "px-8 py-1 mr-4 border border-slate-200 rounded-xl shadow-sm hover:cursor-pointer",
      selected ? 'bg-primary-500' : 'bg-slate-200',
    )}>
      <p className={classNames(
        "col-span-4 text-sm font-normal",
        selected ? 'text-white': 'text-slate-500'
      )}
      >
        {title}
      </p>
    </div>
  )
}